import { ReportBlock, ReportDataSource, ReportTemplate } from '../types';
import { resolveItemsDataRows } from './resolveItemsDataRows';

/**
 * Item names a block reads, for the given source only. A block declaring a
 * different source contributes nothing here — its columns are fetched with that
 * source's rows instead.
 */
const collectBlockItemNames = (
  block: ReportBlock,
  source: ReportDataSource,
  defaultSource: ReportDataSource,
  itemNames: Set<string>
): void => {
  switch (block.kind) {
    case 'row':
      // Blocks nested in a grid cell read data too; missing them here would
      // leave their columns out of the fetch and print them empty.
      block.cells.forEach((cell) =>
        cell.blocks.forEach((cellBlock) =>
          collectBlockItemNames(cellBlock, source, defaultSource, itemNames)
        )
      );
      return;
    case 'fields':
      block.fields.forEach((field) => {
        if ((field.source ?? block.source ?? defaultSource) === source) {
          itemNames.add(field.item);
        }
      });
      return;
    case 'aggregate':
      // 'count' needs no column, just the row count.
      if (
        block.source === source &&
        block.operation !== 'count' &&
        block.item
      ) {
        itemNames.add(block.item);
      }
      return;
    case 'taxBreakdown':
      if (block.source === source) {
        itemNames.add(block.groupBy);
        itemNames.add(block.net);
        itemNames.add(block.gross);
      }
      return;
    default:
      return;
  }
};

/**
 * Every dataset item the template reads from one source. Used to request only
 * the needed columns: a report over six columns must not pull a definition's
 * full item set for every row.
 *
 * The formatItem of a column is added by resolveExportRequestItemNames at fetch
 * time, the same way the export does it.
 */
export const resolveTemplateItemNames = (
  template: ReportTemplate,
  source: ReportDataSource
): string[] => {
  const itemNames = new Set<string>();
  const { header, items, footer } = template.sections;
  const dataRows = resolveItemsDataRows(items);

  dataRows
    .filter((row) => row.source === source)
    .forEach((row) =>
      row.columns.forEach((column) => itemNames.add(column.item))
    );

  header?.blocks.forEach((block) =>
    collectBlockItemNames(block, source, header.source ?? 'master', itemNames)
  );
  footer?.blocks.forEach((block) =>
    collectBlockItemNames(block, source, dataRows[0].source, itemNames)
  );

  return Array.from(itemNames);
};

/** Every source the template reads from, deduplicated. */
export const resolveTemplateSources = (
  template: ReportTemplate
): ReportDataSource[] => {
  const { header, items, footer } = template.sections;
  const dataRows = resolveItemsDataRows(items);
  const sources = new Set<ReportDataSource>(
    dataRows.map((row) => row.source)
  );

  const collectSources = (
    block: ReportBlock,
    defaultSource: ReportDataSource
  ): void => {
    if (block.kind === 'row') {
      block.cells.forEach((cell) =>
        cell.blocks.forEach((cellBlock) =>
          collectSources(cellBlock, defaultSource)
        )
      );
      return;
    }

    if (block.kind === 'fields') {
      block.fields.forEach((field) =>
        sources.add(field.source ?? block.source ?? defaultSource)
      );
      return;
    }

    if (block.kind === 'aggregate' || block.kind === 'taxBreakdown') {
      sources.add(block.source ?? defaultSource);
    }
  };

  header?.blocks.forEach((block) =>
    collectSources(block, header.source ?? 'master')
  );
  footer?.blocks.forEach((block) => collectSources(block, dataRows[0].source));

  return Array.from(sources);
};

/**
 * Sources whose definition/filter metadata is needed even though no row value
 * is read. Keeping these separate avoids fetching an unbounded child result for
 * a single filterValue label.
 */
export const resolveTemplateDefinitionSources = (
  template: ReportTemplate
): ReportDataSource[] => {
  const sources = new Set<ReportDataSource>();
  const collect = (block: ReportBlock): void => {
    if (block.kind === 'row') {
      block.cells.forEach((cell) => cell.blocks.forEach(collect));
      return;
    }
    if (
      block.kind === 'meta' &&
      block.field === 'filterValue' &&
      block.filterSource
    ) {
      sources.add(block.filterSource);
    }
  };

  template.sections.header?.blocks.forEach(collect);
  template.sections.footer?.blocks.forEach(collect);
  return Array.from(sources);
};

/** Subset dataset id behind a 'subset:<id>' source, or undefined for master. */
export const resolveSubsetId = (
  source: ReportDataSource
): string | undefined =>
  source.startsWith('subset:') ? source.slice('subset:'.length) : undefined;
